import { useEffect, useState, useRef } from "react";
import {
  ImageIcon,
  CircleHelp,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Video,
  Download,
  CalendarDays,
  Loader2,
} from "lucide-react";
import axiosInstance from "@/api/axios";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

interface BinMediaViewerProps {
  selectedBin: string[];
  className?: string;
}

interface BinMedia {
  id: number;
  file_url: string;
  description: string;
  uploaded_at: string;
  is_primary: boolean;
  bin_id: string;
  media_type: "image" | "video" | "gif";
  mime_type?: string;
}

type DownloadStatus = "idle" | "downloading" | "success" | "error" | "empty" | "too_large";

const PRESETS = [
  { label: "Custom", value: "custom" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 2 weeks", value: "14d" },
  { label: "Last month", value: "1m" },
  { label: "Last 3 months", value: "3m" },
  { label: "Last 6 months", value: "6m" },
  { label: "Last year", value: "1y" },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function BinMediaViewer({ selectedBin, className }: BinMediaViewerProps) {
  const { t } = useTranslation();

  // ── media carousel state ────────────────────────────────────────────────
  const [mediaItems, setMediaItems] = useState<BinMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasAnyMedia, setHasAnyMedia] = useState(false);

  // ── bulk download state ─────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedPreset, setSelectedPreset] = useState<string>("7d");
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>("idle");
  const [downloadError, setDownloadError] = useState<string>("");
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0); // known if Content-Length present
  const downloadAbortRef = useRef<AbortController | null>(null);

  // ── apply preset ────────────────────────────────────────────────────────
  const applyPreset = (value: string) => {
    setSelectedPreset(value);
    if (value === "custom") return;
    const to = new Date();
    const from = new Date();
    if (value === "7d")  from.setDate(to.getDate() - 7);
    if (value === "14d") from.setDate(to.getDate() - 14);
    if (value === "1m")  from.setMonth(to.getMonth() - 1);
    if (value === "3m")  from.setMonth(to.getMonth() - 3);
    if (value === "6m")  from.setMonth(to.getMonth() - 6);
    if (value === "1y")  from.setFullYear(to.getFullYear() - 1);
    setDateFrom(from.toISOString().split("T")[0]);
    setDateTo(to.toISOString().split("T")[0]);
  };

  // Pre-fill with last 7 days on mount
  useEffect(() => {
    applyPreset("7d");
  }, []);

  // ── fetch media ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedBin.length > 0) {
      fetchBinMedia(selectedBin);
      setCurrentIndex(0);
    } else {
      setMediaItems([]);
      setHasAnyMedia(false);
    }
  }, [selectedBin]);

  const fetchBinMedia = async (binIds: string[]) => {
    try {
      setLoading(true);
      setError(null);
      const responses = await Promise.all(
        binIds.map((binId) => axiosInstance.get(`bin-media/?bin_id=${binId}`))
      );
      const allMedia = responses.flatMap((response) => {
        if (
          response.data &&
          response.data.success &&
          Array.isArray(response.data.data)
        ) {
          return response.data.data.map((item: any) => ({
            ...item,
            bin_id: response.config.url?.split("bin_id=")[1] || "",
          }));
        }
        return [];
      });
      setMediaItems(allMedia);
      setHasAnyMedia(allMedia.length > 0);
    } catch {
      setError(t("failedToLoadBinMedia"));
      setHasAnyMedia(false);
    } finally {
      setLoading(false);
    }
  };

  // ── carousel nav ────────────────────────────────────────────────────────
  const goToPrevious = () =>
    setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  const goToNext = () =>
    setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (mediaItems.length <= 1) return;
      if (e.key === "ArrowLeft") goToPrevious();
      else if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mediaItems.length]);

  // ── dot pagination ──────────────────────────────────────────────────────
  const getVisibleDots = () => {
    const maxVisibleDots = 4;
    if (mediaItems.length <= maxVisibleDots) {
      return {
        dots: Array.from({ length: mediaItems.length }, (_, i) => i),
        showLeftEllipsis: false,
        showRightEllipsis: false,
      };
    }
    let startIndex = Math.max(0, currentIndex - Math.floor(maxVisibleDots / 2));
    let endIndex = Math.min(mediaItems.length - 1, startIndex + maxVisibleDots - 1);
    if (endIndex - startIndex + 1 < maxVisibleDots) {
      startIndex = Math.max(0, endIndex - maxVisibleDots + 1);
    }
    const dots = Array.from({ length: endIndex - startIndex + 1 }, (_, i) => startIndex + i);
    return {
      dots,
      showLeftEllipsis: startIndex > 0,
      showRightEllipsis: endIndex < mediaItems.length - 1,
    };
  };

  // ── bulk download with progress tracking ────────────────────────────────
const handleBulkDownload = async () => {
  if (selectedBin.length === 0) return;
  setDownloadStatus("downloading");
  setDownloadError("");
  setDownloadedBytes(0);
  setTotalBytes(0);

  const controller = new AbortController();
  downloadAbortRef.current = controller;

  const baseURL = axiosInstance.defaults.baseURL || "";
  const authHeaders = axiosInstance.defaults.headers.common as Record<string, string>;

  const buildUrl = (part: number) => {
    const params = new URLSearchParams();
    selectedBin.forEach((id) => params.append("bin_id", id));
    if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
    if (dateTo)   params.set("date_to",   new Date(dateTo).toISOString());
    params.set("part", String(part));
    return `${baseURL}bin-media/bulk-download/?${params.toString()}`;
  };

  const downloadPart = async (part: number, totalParts: number): Promise<number> => {
    const res = await fetch(buildUrl(part), {
      method: "GET",
      credentials: "include",
      headers: authHeaders,
      signal: controller.signal,
    });

    if (res.status === 413) {
      const body = await res.json().catch(() => null);
      throw Object.assign(new Error("too_large"), { body });
    }
    if (res.status === 404) throw new Error("empty");
    if (!res.ok || !res.body) throw new Error(`status_${res.status}`);

    const contentLength = res.headers.get("Content-Length");
    const knownTotal = contentLength ? parseInt(contentLength, 10) : 0;
    if (part === 1) setTotalBytes(knownTotal * totalParts); // rough estimate

    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.length;
        setDownloadedBytes((prev) => prev + value.length);
      }
    }

    // Trigger browser download for this part
    const blob = new Blob(chunks as BlobPart[], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().split("T")[0];
    a.download = totalParts > 1
      ? `bin_media_${date}_part${part}of${totalParts}.zip`
      : `bin_media_${date}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return received;
  };

  try {
    // Fetch part 1 first to discover total_parts from response header
    const firstRes = await fetch(buildUrl(1), {
      method: "GET",
      credentials: "include",
      headers: authHeaders,
      signal: controller.signal,
    });

    if (firstRes.status === 413) {
      const body = await firstRes.json().catch(() => null);
      setDownloadStatus("too_large");
      setDownloadError(body?.message || t("downloadTooLarge", "Selected media is too large."));
      setTimeout(() => setDownloadStatus("idle"), 6000);
      return;
    }
    if (firstRes.status === 404) {
      setDownloadStatus("empty");
      setDownloadError(t("noMediaFoundForTimeframe", "No media found for this timeframe"));
      setTimeout(() => setDownloadStatus("idle"), 4000);
      return;
    }
    if (!firstRes.ok || !firstRes.body) throw new Error(`status_${firstRes.status}`);

    const totalParts = parseInt(firstRes.headers.get("X-Total-Parts") || "1", 10);
    const contentLength = firstRes.headers.get("Content-Length");
    if (contentLength) setTotalBytes(parseInt(contentLength, 10) * totalParts);

    // Stream + save part 1 (already fetched)
    const reader = firstRes.body.getReader();
    const firstChunks: Uint8Array[] = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        firstChunks.push(value);
        setDownloadedBytes((prev) => prev + value.length);
      }
    }
    const blob1 = new Blob(firstChunks as BlobPart[], { type: "application/zip" });
    const url1 = window.URL.createObjectURL(blob1);
    const a1 = document.createElement("a");
    a1.href = url1;
    const date = new Date().toISOString().split("T")[0];
    a1.download = totalParts > 1
      ? `bin_media_${date}_part1of${totalParts}.zip`
      : `bin_media_${date}.zip`;
    document.body.appendChild(a1);
    a1.click();
    document.body.removeChild(a1);
    window.URL.revokeObjectURL(url1);

    // Fetch remaining parts sequentially
    for (let part = 2; part <= totalParts; part++) {
      if (controller.signal.aborted) break;
      await downloadPart(part, totalParts);
    }

    setDownloadedBytes((prev) => prev); // keep final value
    setDownloadStatus("success");
    setTimeout(() => setDownloadStatus("idle"), 3000);

  } catch (err: any) {
    if (err?.name === "AbortError") {
      setDownloadStatus("idle");
      return;
    }
    if (err?.message === "empty") {
      setDownloadStatus("empty");
      setDownloadError(t("noMediaFoundForTimeframe", "No media found for this timeframe"));
      setTimeout(() => setDownloadStatus("idle"), 4000);
      return;
    }
    if (err?.message === "too_large") {
      setDownloadStatus("too_large");
      setDownloadError(err?.body?.message || t("downloadTooLarge", "Selected media is too large."));
      setTimeout(() => setDownloadStatus("idle"), 6000);
      return;
    }
    setDownloadStatus("error");
    setDownloadError(t("downloadFailed", "Download failed"));
    setTimeout(() => setDownloadStatus("idle"), 4000);
  } finally {
    downloadAbortRef.current = null;
  }
};

  const cancelDownload = () => {
    downloadAbortRef.current?.abort();
  };

  // ── progress bar width ──────────────────────────────────────────────────
  // If Content-Length is known: real percentage, capped at 98% until done.
  // If unknown: grows logarithmically against a 500 MB soft cap so it never
  // restarts and keeps visibly advancing as bytes come in.
  const getProgressWidth = () => {
    if (downloadStatus === "success") return 100;
    if (downloadStatus !== "downloading") return 0;
    if (totalBytes > 0) {
      return Math.min((downloadedBytes / totalBytes) * 100, 98);
    }
    // Logarithmic growth: fast at first, slows as it approaches ~95%
    const SOFT_CAP = 500 * 1024 * 1024; // 500 MB
    const ratio = Math.min(downloadedBytes / SOFT_CAP, 1);
    return Math.max(2, Math.log1p(ratio * (Math.E - 1)) * 95);
  };

  // ── shared container classes ────────────────────────────────────────────
  const containerClasses = cn(
    "lg:col-span-2 sm:rounded-lg bg-muted/50 p-4 flex flex-col h-full min-w-0 w-full",
    className,
    "aspect-square sm:aspect-[16/10]"
  );

  // ── header ──────────────────────────────────────────────────────────────
  const Header = ({ showDownload = false }: { showDownload?: boolean }) => (
    <div className="flex justify-between items-center mb-2 shrink-0">
      <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
        {t("binMedia")}
      </h4>
      <div className="flex items-center gap-2">
        {showDownload && selectedBin.length > 0 && (
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showDownloadPanel ? "secondary" : "ghost"}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setShowDownloadPanel((v) => !v)}
                  aria-label={t("downloadTimelapse")}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("downloadTimelapse")}</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <CircleHelp className="h-5 w-5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {showDownload
                  ? t("displaysMediaImagesGifsVideosForSelectedBins")
                  : t("displaysMediaUploadedForSelectedBins")}
              </p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
    </div>
  );

  // ── download panel ──────────────────────────────────────────────────────
  const DownloadPanel = () => (
    <div className="shrink-0 mb-3 rounded-lg border border-border bg-card shadow-sm p-4 flex flex-col gap-4">
      {/* Title row */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          {t("Select Period of Time")}
        </p>
        {(dateFrom || dateTo) && downloadStatus === "idle" && (
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setSelectedPreset("custom");
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("clear")}
          </button>
        )}
      </div>

      {/* Preset pills */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            onClick={() => applyPreset(p.value)}
            disabled={downloadStatus === "downloading"}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all border disabled:opacity-50 disabled:cursor-not-allowed ${
              selectedPreset === p.value
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-muted text-muted-foreground border-transparent hover:border-border hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      {selectedPreset === "custom" && (
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{t("from")}</Label>
            <Input
              type="date"
              className="h-8 text-xs w-36"
              value={dateFrom}
              max={dateTo || undefined}
              disabled={downloadStatus === "downloading"}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{t("to")}</Label>
            <Input
              type="date"
              className="h-8 text-xs w-36"
              value={dateTo}
              min={dateFrom || undefined}
              max={new Date().toISOString().split("T")[0]}
              disabled={downloadStatus === "downloading"}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Date range summary */}
      {dateFrom && dateTo && downloadStatus !== "downloading" && (
        <p className="text-xs text-muted-foreground">
          📅 {new Date(dateFrom).toLocaleDateString()} → {new Date(dateTo).toLocaleDateString()}
        </p>
      )}

      {/* Download button + status */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          {downloadStatus !== "downloading" ? (
            <Button
              variant="default"
              size="sm"
              className="gap-2 text-xs"
              onClick={handleBulkDownload}
              disabled={!dateFrom || !dateTo}
            >
              <Download className="h-3.5 w-3.5" />
              {t("Download Zip")}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-xs"
              onClick={cancelDownload}
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("cancel", "Cancel")}
            </Button>
          )}

          {downloadStatus === "success" && (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
              <span>✓</span>
              <span>{t("downloadStarted", "Download complete")}</span>
            </span>
          )}
          {(downloadStatus === "error" || downloadStatus === "empty" || downloadStatus === "too_large") && (
            <span className="text-xs text-destructive">{downloadError}</span>
          )}
        </div>

        {/* Progress bar — grows forward based on actual bytes, never restarts.
            Uses real percentage if Content-Length is present, otherwise
            logarithmic growth against a 500 MB soft cap.
            Snaps to 100% when download completes. */}
        {(downloadStatus === "downloading" || downloadStatus === "success") && (
          <div className="flex flex-col gap-1.5">
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-300 ease-out",
                  downloadStatus === "success" ? "bg-green-500" : "bg-primary"
                )}
                style={{ width: `${getProgressWidth()}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {downloadStatus === "success"
                ? `✓ ${formatBytes(downloadedBytes)} — done`
                : totalBytes > 0
                  ? `${formatBytes(downloadedBytes)} of ${formatBytes(totalBytes)}`
                  : `${formatBytes(downloadedBytes)} received…`}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ── loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={containerClasses}>
        <Header />
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full">
          {/* Skeleton — same layout as the loaded carousel: media frame
              with prev/next arrow placeholders, a caption bar with
              description/date on the left and a bin-tag pill on the
              right, and dot-pagination underneath. */}
          <div
            className="relative h-full w-full motion-safe:animate-pulse"
            aria-busy="true"
          >
            <span className="sr-only" role="status">{t("loadingMedia")}</span>

            <div className="border rounded-lg overflow-hidden bg-background h-full w-full flex flex-col">
              <div className="relative flex-1 min-h-0 w-full bg-gray-200 dark:bg-slate-700">
                <div className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/10 dark:bg-white/10" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/10 dark:bg-white/10" />
              </div>

              <div className="p-3 shrink-0 bg-background border-t">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1 mr-2 space-y-1.5">
                    <div className="h-3.5 w-32 rounded bg-gray-200 dark:bg-slate-700" />
                    <div className="h-3 w-24 rounded bg-gray-200 dark:bg-slate-700" />
                  </div>
                  <div className="h-5 w-16 rounded bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                </div>
              </div>
            </div>

            <div className="absolute bottom-[52px] left-1/2 -translate-x-1/2 flex items-center gap-1">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-2 w-2 rounded-full bg-gray-300 dark:bg-slate-600" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── error ───────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={containerClasses}>
        <Header />
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full flex items-center justify-center">
          <div className="text-destructive">{error}</div>
        </div>
      </div>
    );
  }

  // ── no bin selected ─────────────────────────────────────────────────────
  if (selectedBin.length === 0) {
    return (
      <div className={containerClasses}>
        <Header />
        <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full flex items-center justify-center">
          <div className="text-muted-foreground">{t("selectBinsToViewMedia")}</div>
        </div>
      </div>
    );
  }

  // ── no media ────────────────────────────────────────────────────────────
  if (!hasAnyMedia) {
    return null;
  }

  const { dots, showLeftEllipsis, showRightEllipsis } = getVisibleDots();

  // ── main render ─────────────────────────────────────────────────────────
  return (
    <div className={containerClasses}>
      <Header showDownload />

      {/* Download panel */}
      {showDownloadPanel && <DownloadPanel />}

      {/* Media area */}
      <div className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0 w-full min-w-0 relative">
        <div className="relative h-full w-full">
          {/* Prev / Next arrows */}
          {mediaItems.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={goToNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* Carousel track */}
          <div className="h-full w-full overflow-hidden rounded-md bg-background">
            <div
              className="h-full w-full flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {mediaItems.map((media) => (
                <div
                  key={`${media.bin_id}-${media.id}`}
                  className="h-full shrink-0 basis-full relative"
                >
                  <div className="border rounded-lg overflow-hidden bg-background group relative h-full w-full flex flex-col">
                    {/* Expand button */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="absolute right-2 top-2 z-10 p-1 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Maximize2 className="h-4 w-4 text-white" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 flex items-center justify-center">
                        <DialogTitle className="sr-only">
                          {t("mediaViewer")}
                        </DialogTitle>
                        {media.media_type === "video" ? (
                          <div className="w-full h-full flex items-center justify-center bg-black">
                            <video controls autoPlay className="max-w-full max-h-[80vh]">
                              <source src={media.file_url} type={media.mime_type || "video/mp4"} />
                              {t("yourBrowserDoesNotSupportVideo")}
                            </video>
                          </div>
                        ) : (
                          <img
                            src={media.file_url}
                            alt={media.description || t("noDescription")}
                            className="max-w-full max-h-[80vh] object-contain"
                          />
                        )}
                      </DialogContent>
                    </Dialog>

                    {/* Media content */}
                    <div className="flex-1 relative min-h-0 w-full overflow-hidden bg-black/5">
                      {media.file_url ? (
                        media.media_type === "video" ? (
                          <video
                            controls
                            className="absolute inset-0 w-full h-full object-contain bg-black"
                          >
                            <source src={media.file_url} type={media.mime_type || "video/mp4"} />
                            {t("yourBrowserDoesNotSupportHTML5Video")}
                          </video>
                        ) : (
                          <img
                            src={media.file_url}
                            alt={media.description || t("noDescription")}
                            className="w-full h-full object-contain"
                          />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          {media.media_type === "video" ? (
                            <Video className="h-12 w-12 text-gray-400" />
                          ) : (
                            <ImageIcon className="h-12 w-12 text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Caption bar */}
                    <div className="p-3 shrink-0 bg-background border-t">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1 mr-2">
                          <p className="text-sm font-medium truncate" title={media.description}>
                            {media.description || t("noDescription")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("uploaded")}:{" "}
                            {new Date(media.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">
                            {t("bin")}: {media.bin_id}
                          </span>
                        </div>
                      </div>
                      {media.is_primary && (
                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded">
                          {t("primary")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot navigation */}
          {mediaItems.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex justify-center items-center gap-1 z-20 pointer-events-none">
              <div className="bg-black/30 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 pointer-events-auto">
                {showLeftEllipsis && (
                  <span className="text-xs text-white/80 mx-1">...</span>
                )}
                {dots.map((dotIndex) => (
                  <button
                    key={dotIndex}
                    className={`h-2 w-2 rounded-full transition-all duration-200 ease-in-out shadow-sm ${
                      currentIndex === dotIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/80"
                    }`}
                    onClick={() => setCurrentIndex(dotIndex)}
                    aria-label={t("goToMedia", { number: dotIndex + 1 })}
                  />
                ))}
                {showRightEllipsis && (
                  <span className="text-xs text-white/80 mx-1">...</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}